import type { InterestTermRow } from "@/api/types";
import { t } from "@/i18n";
import { formatDecimal, formatInt } from "@/lib/format";
import { trendLabel } from "@/lib/vocabulary";

interface Props {
  /** The ranking's name, for screen readers and tests. */
  title: string;
  rows: readonly InterestTermRow[];
  /** The comparison's column head; null without a comparison, which leaves
   * out the comparison and trend columns. */
  baselineHead: string | null;
  empty: string;
}

/** An interest ranking: the terms conversations asked about, most asked first,
 * the comparison's count beside each and a trend badge (neu, steigt, sinkt;
 * none for no clear change). */
export function InterestTerms({ title, rows, baselineHead, empty }: Props) {
  if (rows.length === 0) return <div class="kpi-sub">{empty}</div>;
  const compared = baselineHead !== null;
  return (
    <div class="qc-scroll">
      <table class="table interest-terms" aria-label={title}>
        <thead>
          <tr>
            <th>{t("terms.col.term")}</th>
            <th>{t("terms.col.conversations")}</th>
            {compared && <th>{baselineHead}</th>}
            {compared && <th>{t("terms.col.trend")}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.term}>
              <td>{row.term}</td>
              <td>{formatInt(row.sessions)}</td>
              {compared && <td>{row.baseline === null ? "—" : formatDecimal(row.baseline)}</td>}
              {compared && (
                <td>
                  {row.trend ? (
                    <span class={`trend-badge trend-${row.trend}`}>{trendLabel(row.trend)}</span>
                  ) : (
                    <span class="muted">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
